/**
 * @voltxampere/web — Builtin command barrel (Sprint 22 C3)
 *
 * Concatenates every category's command array and exposes
 * `registerBuiltinCommands(registry)` for the boot path. The boot
 * path (apps/web/src/main.ts) calls this exactly once after the
 * Sprint 11 store + Sprint 21 persistence layers initialise — the
 * registry is process-wide, so a second call would throw on the
 * first duplicate id.
 *
 * Categories register in BLUEPRINT §14.1 canonical order so palette
 * results break ties alphabetically by category when `fuzzyScore`
 * returns a draw.
 *
 * Copyright © 2026 360 Studio
 * Proprietary — All rights reserved.
 */

import type { Command } from "../types.js";
import type { CommandRegistry } from "../registry.js";
import { FILE_COMMANDS } from "./file.js";
import { EDIT_COMMANDS } from "./edit.js";
import { VIEW_COMMANDS } from "./view.js";
import { TOOL_COMMANDS } from "./tool.js";
import { PLACE_COMMANDS } from "./place.js";
import { SIMULATION_COMMANDS } from "./simulation.js";
import { PANE_COMMANDS } from "./pane.js";
import { PRESET_COMMANDS } from "./preset.js";
import { HELP_COMMANDS } from "./help.js";
import { DEVELOPER_COMMANDS } from "./developer.js";

/** Flat array of every Sprint 22 builtin command, in canonical order. */
export const BUILTIN_COMMANDS: readonly Command[] = [
  ...FILE_COMMANDS,
  ...EDIT_COMMANDS,
  ...VIEW_COMMANDS,
  ...TOOL_COMMANDS,
  ...PLACE_COMMANDS,
  ...SIMULATION_COMMANDS,
  ...PANE_COMMANDS,
  ...PRESET_COMMANDS,
  ...HELP_COMMANDS,
  ...DEVELOPER_COMMANDS,
];

/**
 * Register every builtin command on the supplied registry. Boot
 * code passes the singleton `commandRegistry`; tests pass a fresh
 * `new CommandRegistry()` so they can assert on `size()` without
 * polluting other suites.
 *
 * Returns the count of commands registered — useful for the "100+
 * commands ready" boot log line.
 */
export function registerBuiltinCommands(registry: CommandRegistry): number {
  for (const cmd of BUILTIN_COMMANDS) {
    registry.register(cmd);
  }
  return BUILTIN_COMMANDS.length;
}
